import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { App, Alert, Typography } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useSessionToken,
  useIsGuest,
  useEnterAssessment,
  useRevokedKickOut,
  type PackageRevokedNavState,
} from '@/hooks';
import { motion } from 'framer-motion';
import { AnswerMode } from '@/store/interviewStore';
import { HtsSubmissionSummaryStatus } from '@evaltick/shared';
import { InterviewTemplatesService, AssessmentService, HtsService, BaseAPIClient } from '@/services/api';
import type { InterviewTemplate, HtsAdminFollowUpItem } from '@/services/api';
import { Button } from '@/components/Button';
import { SetupWizard } from './SetupWizard';
import { PastSessionsList } from './PastSessionsList';
import { AdminFollowUpsModal } from './AdminFollowUps';
import { HistoryItemKind, type HistoryItem } from './historyItem';
import {
  buildAssessmentsByTemplate,
  partsOf,
  computeSteps,
  autoSelectPart,
  defaultModeFor,
  guestFollowUpBanner,
  type PartKey,
} from './interviewStatus';
import { useSessionWizardStore } from '@/store';
import styles from './Sessions.module.scss';

const { Title, Text } = Typography;

const HISTORY_PAGE_SIZE = 20;

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: 'easeOut' as const } },
};

export const Sessions: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useSessionToken();
  const isGuest = useIsGuest();
  const { notification } = App.useApp();
  const enterAssessment = useEnterAssessment();
  const handleIfRevoked = useRevokedKickOut();

  const [templates, setTemplates] = useState<InterviewTemplate[]>([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [templatesReloadNonce, setTemplatesReloadNonce] = useState(0);
  // Scopes the click/spinner to the row being resumed, not the whole list.
  const [resumingAssessmentId, setResumingAssessmentId] = useState<string | null>(null);

  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  // False until history first loads, so a guest with a submitted HTS doesn't flash the plain thank-you.
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Reply drafts live here so closing and reopening the modal keeps them; a reload loses them.
  const [followUpItems, setFollowUpItems] = useState<HtsAdminFollowUpItem[] | null>(null);
  // Separate from the items so the modal can animate closed.
  const [followUpsOpen, setFollowUpsOpen] = useState(false);
  const [followUpDrafts, setFollowUpDrafts] = useState<Record<string, string>>({});
  const [loadingFollowUps, setLoadingFollowUps] = useState(false);
  const [submittingFollowUps, setSubmittingFollowUps] = useState(false);

  // Non-guest wizard state, lifted here (not local to SetupWizard) so
  // PastSessionsList's Resume/Try-again actions can load a past attempt
  // straight into it — see loadIntoCard below. Persisted (see
  // sessionWizardStore) so it survives navigating away to Packages and back.
  const wizard = useSessionWizardStore((s) => s.wizard);
  const setWizard = useSessionWizardStore((s) => s.setWizard);
  const setupCardRef = useRef<HTMLDivElement>(null);

  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const token = await getToken();
      if (!token) return;
      const [assessments, htsSubmissions] = await Promise.all([
        new AssessmentService(token).listMine(1, HISTORY_PAGE_SIZE),
        new HtsService(token).listMySubmissions(1, HISTORY_PAGE_SIZE),
      ]);
      const items: HistoryItem[] = [
        ...assessments.data.map((item): HistoryItem => ({
          kind: HistoryItemKind.Assessment,
          key: `${HistoryItemKind.Assessment}-${item.id}`,
          date: item.completedAt ?? item.createdAt,
          item,
        })),
        ...htsSubmissions.data.map((item): HistoryItem => ({
          kind: HistoryItemKind.Hts,
          key: `${HistoryItemKind.Hts}-${item.id}`,
          date: item.submittedAt ?? item.updatedAt,
          item,
        })),
      ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setHistory(items);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingHistory(false);
      setHistoryLoaded(true);
    }
  }, [getToken]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // A ref (not just the location.state clear below) guards against showing
  // this twice — React 18 StrictMode intentionally mounts effects twice in
  // dev, and the state-clearing navigate() below doesn't commit synchronously
  // enough to prevent a second read of the stale state on that replay.
  const htsBannerShownRef = useRef(false);
  useEffect(() => {
    if (htsBannerShownRef.current) return;
    const state = location.state as { htsJustSubmitted?: boolean } | null;
    if (state?.htsJustSubmitted) {
      htsBannerShownRef.current = true;
      notification.success({ message: 'Questionnaire submitted — you can now start your session.' });
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // A kick-out lands here with this state, including from the hub itself where
  // nothing remounts — so refetch. Skipped at mount; the initial loads cover it.
  const packageRevoked = (location.state as PackageRevokedNavState | null)?.packageRevoked === true;
  const hasMountedRef = useRef(false);
  useEffect(() => {
    const alreadyMounted = hasMountedRef.current;
    hasMountedRef.current = true;
    if (!packageRevoked) return;
    if (alreadyMounted) {
      setTemplatesReloadNonce((n) => n + 1);
      void fetchHistory();
    }
    navigate(location.pathname, { replace: true, state: {} });
  }, [packageRevoked]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoadingTemplates(true);
      setLoadError(null);
      try {
        const token = await getToken();
        if (!token) return;
        const owned = await new InterviewTemplatesService(token).list();
        if (!cancelled) {
          setTemplates(owned);
          if (owned.length > 0) setSelectedTemplateId(owned[0].id);
        }
      } catch (err) {
        if (!cancelled) setLoadError(BaseAPIClient.parseErrorMsg(err));
      } finally {
        if (!cancelled) setLoadingTemplates(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [getToken, templatesReloadNonce]);

  // The one signal InterviewTemplate itself doesn't expose in bulk: whether a
  // Competency attempt is currently in progress (only "ever completed" is a
  // bulk field) — derived from the history already being fetched above.
  const assessmentsByTemplate = useMemo(
    () =>
      buildAssessmentsByTemplate(
        history.filter((h) => h.kind === HistoryItemKind.Assessment).map((h) => h.item)
      ),
    [history]
  );

  const loadIntoCard = (templateId: string, part: PartKey, mode: 'retry' | 'resume') => {
    const template = templates.find((t) => t.id === templateId);
    if (!template) return;

    if (mode === 'retry') {
      const autoPart = autoSelectPart(template);
      setWizard((prev) => ({
        templateId,
        part: autoPart,
        mode: defaultModeFor(autoPart),
        step: 0,
        flashNonce: prev.flashNonce + 1,
      }));
      return;
    }

    setWizard((prev) => ({
      templateId,
      part,
      mode: defaultModeFor(part),
      step: Math.max(0, computeSteps(template, part).indexOf('preview')),
      flashNonce: prev.flashNonce + 1,
    }));
  };

  const selectedSet = templates.find((template) => template.id === selectedTemplateId) ?? null;

  // A guest always has exactly one redeemed template, so this only ever
  // applies to a signed-in candidate who hasn't purchased anything yet —
  // still drives the page header's title/subtitle treatment (out of scope
  // to redesign further; the wizard now owns its own empty-state rendering).
  const showEmptyState = !isGuest && !loadingTemplates && !loadError && templates.length === 0;

  // A guest who's used both of their one-shot attempts (see
  // guestAttemptGuard.ts) has nothing left to start on this template.
  const guestExhausted =
    isGuest &&
    !!selectedSet &&
    (selectedSet.htsRequired
      ? selectedSet.htsEverSubmitted && selectedSet.assessmentEverCompleted
      : selectedSet.assessmentEverCompleted);

  const guestDoneParts = isGuest && selectedSet
    ? partsOf(selectedSet, assessmentsByTemplate).filter((p) => p.status === 'done')
    : [];
  const guestJustCompletedPart = !guestExhausted && guestDoneParts.length === 1 ? guestDoneParts[0] : null;

  // The guest's submitted HTS for the selected template, which carries the follow-up status.
  const guestHtsItem =
    isGuest && selectedSet?.htsRequired
      ? history.find(
          (h) =>
            h.kind === HistoryItemKind.Hts &&
            h.item.interviewTemplateId === selectedSet.id &&
            h.item.status !== HtsSubmissionSummaryStatus.Draft
        )
      : undefined;
  const followUpBanner =
    guestHtsItem?.kind === HistoryItemKind.Hts
      ? guestFollowUpBanner(
        guestHtsItem.item.adminFollowUpStatus,
        !!selectedSet?.assessmentEverCompleted,
        guestHtsItem.item.guestLoginEndsAt
      )
      : null;
  const awaitingFollowUpStatus = isGuest && !!selectedSet?.htsEverSubmitted && !historyLoaded;

  const handleOpenFollowUps = async () => {
    if (!guestHtsItem || loadingFollowUps) return;
    setLoadingFollowUps(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const result = await new HtsService(token).getAdminFollowUps(guestHtsItem.item.id);
      setFollowUpItems(result.items);
      setFollowUpsOpen(true);
    } catch (err) {
      if (handleIfRevoked(err)) return;
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setLoadingFollowUps(false);
    }
  };

  const handleSubmitFollowUps = async () => {
    if (!guestHtsItem || !followUpItems || submittingFollowUps) return;
    setSubmittingFollowUps(true);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      await new HtsService(token).submitAdminFollowUps(
        guestHtsItem.item.id,
        followUpItems.map((i) => ({ questionId: i.questionId, answer: (followUpDrafts[i.questionId] ?? '').trim() }))
      );
      setFollowUpsOpen(false);
      setFollowUpDrafts({});
      notification.success({ message: 'Your answers were submitted.' });
      await fetchHistory();
    } catch (err) {
      if (handleIfRevoked(err)) return;
      // Already answered in another tab; just refresh.
      if (BaseAPIClient.isApiError(err) && err.status === 409) {
        setFollowUpsOpen(false);
        setFollowUpDrafts({});
        await fetchHistory();
        return;
      }
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    } finally {
      setSubmittingFollowUps(false);
    }
  };

  const handleResumeAssessment = async (assessmentId: string) => {
    if (resumingAssessmentId) return;
    setResumingAssessmentId(assessmentId);
    try {
      const token = await getToken();
      if (!token) throw new Error('Not authenticated');
      const assessment = await new AssessmentService(token).getAssessment(assessmentId);
      await enterAssessment(assessment, { defaultAnswerMode: AnswerMode.Text });
    } catch (err) {
      setResumingAssessmentId(null);
      if (handleIfRevoked(err)) return;
      notification.error({ message: BaseAPIClient.parseErrorMsg(err) });
    }
  };

  return (
    <motion.div
      className={styles.page}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {isGuest && followUpBanner && (
        <motion.div variants={itemVariants}>
          <Alert
            className={styles.followUpAlert}
            type={followUpBanner.type}
            showIcon
            message={followUpBanner.message}
            description={followUpBanner.description}
            action={
              followUpBanner.canReply ? (
                <Button appearance="solid" accent="yellow" loading={loadingFollowUps} onClick={handleOpenFollowUps}>
                  Reply
                </Button>
              ) : undefined
            }
          />
        </motion.div>
      )}

      {isGuest && !followUpBanner && !awaitingFollowUpStatus && !guestExhausted && guestJustCompletedPart && (
        <motion.div variants={itemVariants}>
          <Alert
            type="success"
            showIcon
            message={`Thank you for completing your ${guestJustCompletedPart.short} assessment.`}
            description="Your results will be forwarded to the requesting agency."
          />
        </motion.div>
      )}

      {!isGuest && (
        <motion.div className={styles.header} variants={itemVariants}>
          <div>
            <Title level={3} className={`${styles.pageTitle} ${styles.pageTitleLarge}`}>
              Sessions
            </Title>
            {!showEmptyState && (
              <Text className={styles.pageSub}>Start a new practice session or review past attempts.</Text>
            )}
          </div>
        </motion.div>
      )}

      {isGuest && guestExhausted ? (
        !followUpBanner &&
        !awaitingFollowUpStatus && (
          <motion.div variants={itemVariants}>
            <Alert
              type="success"
              showIcon
              message="Thank you for completing your assessment."
              description="Your results will be forwarded to the requesting agency."
            />
          </motion.div>
        )
      ) : (
        <motion.div variants={itemVariants}>
          <SetupWizard
            templates={templates}
            assessmentsByTemplate={assessmentsByTemplate}
            loadingTemplates={loadingTemplates}
            loadError={loadError}
            onRetryLoad={() => setTemplatesReloadNonce((n) => n + 1)}
            wizard={wizard}
            setWizard={setWizard}
            cardRef={setupCardRef}
          />
        </motion.div>
      )}

      {/* A guest never sees their results, so there's no history to open. */}
      {!isGuest && (
        <>
          <motion.div className={styles.sessionHistorySection} variants={itemVariants}>
            <Title level={3} className={`${styles.pageTitle} ${styles.pageTitleLarge}`}>
              Past sessions
            </Title>
          </motion.div>

          <motion.div variants={itemVariants}>
            <PastSessionsList
              history={history}
              loadingHistory={loadingHistory}
              onLoadIntoCard={loadIntoCard}
              onResumeAssessment={handleResumeAssessment}
              resumingAssessmentId={resumingAssessmentId}
            />
          </motion.div>
        </>
      )}

      {followUpItems && (
        <AdminFollowUpsModal
          open={followUpsOpen}
          items={followUpItems}
          drafts={followUpDrafts}
          onDraftChange={(questionId, value) => setFollowUpDrafts((prev) => ({ ...prev, [questionId]: value }))}
          onSubmit={handleSubmitFollowUps}
          onClose={() => setFollowUpsOpen(false)}
        />
      )}
    </motion.div>
  );
};

export default Sessions;
